import { useMemo } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';

import dateFormat from 'dateformat';
import { useFormikContext } from 'formik';

import { ButtonSplitGroup } from '@components/Buttons';
import { updateQueryStringOnFieldChange } from '@components/Filters/Filters';
import { BootstrapGenericTable } from '@components/Table/Table';
import { OPEN_ORDER_STATUSES } from '@config/const';

const getDetailsURL = (statusID, uuid) =>
  OPEN_ORDER_STATUSES.includes(statusID)
    ? `/order/open/${uuid}/details`
    : `/order/history/${uuid}/details`;

const renderProfileHeader = (
  formikContext,
  searchParams,
  setSearchParams,
  profilesData
) => {
  const { handleChange, handleBlur, values } = formikContext;
  return (
    <Form.Group controlId="formGroupProfile">
      <Form.Label>End user</Form.Label>
      <Form.Select
        name="profile"
        size="sm"
        value={values.profile}
        onChange={(e) => {
          handleChange(e);
          updateQueryStringOnFieldChange(e, { searchParams, setSearchParams });
        }}
        onBlur={handleBlur}
      >
        <option value="">Search end user</option>
        {profilesData.length > 0 ? (
          <>
            {profilesData.map((item) => (
              <option key={item.value} value={item.value}>
                {item.name}
              </option>
            ))}
          </>
        ) : null}
      </Form.Select>
    </Form.Group>
  );
};

const renderStatusHeader = (
  formikContext,
  searchParams,
  setSearchParams,
  orderStatuses
) => {
  const { handleChange, handleBlur, values } = formikContext;
  return (
    <Form.Group controlId="fromGroupStatus">
      <Form.Label>Status</Form.Label>
      <Form.Select
        name="status"
        size="sm"
        value={values.status}
        onChange={(e) => {
          handleChange(e);
          updateQueryStringOnFieldChange(e, { searchParams, setSearchParams });
        }}
        onBlur={handleBlur}
      >
        <option value="">Search status</option>
        {orderStatuses.length > 0 ? (
          <>
            {orderStatuses.map((item) => (
              <option key={item.id} value={item.id}>
                {item.description}
              </option>
            ))}
          </>
        ) : null}
      </Form.Select>
    </Form.Group>
  );
};

const renderOrderCell = ({ row }) => {
  const { reference_number: referenceNumber, uuid } = row.values;
  const { status } = row.original;
  const detailsURL = getDetailsURL(status.id, uuid);
  return (
    <Link variant="didxl" size="sm" to={detailsURL}>
      {referenceNumber}
    </Link>
  );
};

const renderActionCell = ({ row }) => {
  const { uuid } = row.values;
  const { status } = row.original;
  const detailsURL = getDetailsURL(status.id, uuid);
  const { sof_document: sofLocation, sof_compulsory: hasSof } = row.original;
  return (
    <ButtonSplitGroup>
      <Button as={Link} variant="didxl" size="sm" to={detailsURL}>
        Details
      </Button>
      {sofLocation ? (
        <Button
          as={Link}
          variant="didxl"
          size="sm"
          to={sofLocation}
          disabled={!hasSof}
          target="_blank"
        >
          SOF
        </Button>
      ) : null}
    </ButtonSplitGroup>
  );
};

const renderOpenItemsCell = ({ row }) => {
  const {
    count_bo: countBackorder,
    pending_docs: pendingDocs,
    rejected_documents: rejectedDocs,
  } = row.original;

  const textArray = [];

  if (countBackorder > 0) {
    textArray.push(<span key="backorder">Backorder</span>);
  }
  if (pendingDocs) {
    textArray.push(
      <span key="missingDocuments" className="textRed">
        Missing documents
      </span>
    );
  }
  if (rejectedDocs) {
    textArray.push(
      <span key="rejectedDocuments" className="textRed">
        Rejected documents
      </span>
    );
  }
  if (textArray.length === 0) {
    return null;
  }

  return <>{textArray.reduce((prev, curr) => [prev, ', ', curr])}</>;
};

export const ListOrdersTable = ({
  useData,
  searchParams,
  setSearchParams,
  profilesData,
  orderStatuses,
  tableCardHeader,
}) => {
  const profile = searchParams.get('profile');
  const status = searchParams.get('status');
  const country = searchParams.get('country');
  const area = country ? searchParams.get('area') : '';
  const did = searchParams.get('did');
  const filterData = {
    country,
    area,
    did,
    profile,
    status,
  };

  const columns = useMemo(
    () => [
      {
        id: 'country',
        Header: 'Country',
        accessor: 'country.description',
      },
      {
        id: 'area',
        Header: 'City/Area Code',
        accessor: 'area.description',
        Cell: ({ row }) =>
          `${row.original.area.description} (${row.original.area.code})`,
      },
      {
        id: 'uuid',
        accessor: 'uuid',
      },
      {
        Header: 'Order',
        accessor: 'reference_number',
        className: 'col-md-1',
        Cell: renderOrderCell,
      },
      { Header: 'Type', accessor: 'type', className: 'col-md-1' },
      {
        id: 'profileName',
        Header: () => {
          const formikContext = useFormikContext();
          return renderProfileHeader(
            formikContext,
            searchParams,
            setSearchParams,
            profilesData
          );
        },
        Cell: ({ value }) => {
          if (!value) {
            return null;
          }
          return value.map(({ name }) => name).toString();
        },
        accessor: 'profiles',
        className: 'col-md-2',
      },
      {
        Header: 'Date Committed',
        accessor: 'date_commit',
        Cell: ({ value }) => {
          const date = new Date(value);
          return dateFormat(date, 'defaultDate');
        },
        className: 'col-md-1',
      },
      {
        Header: '#DIDs/CH',
        className: 'col-md-1',
        Cell: ({ row }) =>
          `${row.original.count_did} / ${row.original.count_channel}`,
      },
      {
        id: 'status',
        Header: () => {
          const formikContext = useFormikContext();
          return renderStatusHeader(
            formikContext,
            searchParams,
            setSearchParams,
            orderStatuses
          );
        },
        accessor: 'status.description',
        className: 'col-md-2',
      },
      {
        Header: 'Open items',
        className: 'col-md-2',
        accessor: 'openItems',
        Cell: renderOpenItemsCell,
      },
      {
        Header: 'Action',
        accessor: 'action',
        className: 'col-md-1 text-center',
        Cell: renderActionCell,
      },
    ],
    [country, area, did, profilesData, orderStatuses]
  );
  return (
    <Card>
      <Card.Header className="bg-didxl text-white">
        {tableCardHeader}
      </Card.Header>
      <Card.Body>
        <BootstrapGenericTable
          columns={columns}
          useData={useData}
          filterData={filterData}
          tableProps={() => ({
            responsive: true,
            bordered: true,
            className: 'normalFontSize',
          })}
          hiddenColumns="area,country,uuid"
          paginated
        />
      </Card.Body>
    </Card>
  );
};

export const ListIncompleteOrdersTable = ({
  useData,
  searchParams,
  setSearchParams,
  profilesData,
  orderStatuses,
}) => {
  const location = useLocation();
  const fromCheckout = location?.state?.fromCheckout;

  const profile = searchParams.get('profile');
  const status = searchParams.get('status');
  const country = searchParams.get('country');
  const area = country ? searchParams.get('area') : '';
  const did = searchParams.get('did');
  const filterData = {
    country,
    area,
    did,
    profile,
    status,
  };

  const renderIncompleteActionCell = () => {
    const detailsURL = `/profiles/existing`;
    return (
      <ButtonSplitGroup>
        <Button as={Link} variant="didxl" size="sm" to={detailsURL}>
          Complete end users
        </Button>
      </ButtonSplitGroup>
    );
  };

  const columns = useMemo(
    () => [
      {
        id: 'country',
        Header: 'Country',
        accessor: 'country.description',
      },
      {
        id: 'area',
        Header: 'City/Area Code',
        accessor: 'area.description',
        Cell: ({ row }) =>
          `${row.original.area.description} (${row.original.area.code})`,
      },
      {
        id: 'uuid',
        accessor: 'uuid',
      },
      {
        Header: 'Order',
        accessor: 'reference_number',
        className: 'col-md-1',
        Cell: renderOrderCell,
      },
      { Header: 'Type', accessor: 'type', className: 'col-md-1' },
      {
        id: 'profileName',
        Header: () => {
          const formikContext = useFormikContext();
          return renderProfileHeader(
            formikContext,
            searchParams,
            setSearchParams,
            profilesData
          );
        },
        Cell: ({ value }) => {
          if (!value) {
            return null;
          }
          return value.map(({ name }) => name).toString();
        },
        accessor: 'profiles',
        className: 'col-md-2',
      },
      {
        Header: 'Date Committed',
        accessor: 'date_commit',
        Cell: ({ value }) => {
          const date = new Date(value);
          return dateFormat(date, 'defaultDate');
        },
        className: 'col-md-1',
      },
      {
        Header: '#DIDs/CH',
        className: 'col-md-1',
        Cell: ({ row }) =>
          `${row.original.count_did} / ${row.original.count_channel}`,
      },
      {
        id: 'status',
        Header: () => {
          const formikContext = useFormikContext();
          return renderStatusHeader(
            formikContext,
            searchParams,
            setSearchParams,
            orderStatuses
          );
        },
        accessor: 'status.description',
        className: 'col-md-2',
      },
      {
        Header: 'Open items',
        className: 'col-md-2',
        accessor: 'openItems',
        Cell: renderOpenItemsCell,
      },
      {
        Header: 'Action',
        accessor: 'action',
        className: 'col-md-1 text-center',
        Cell: renderIncompleteActionCell,
      },
    ],
    [country, area, did, profilesData, orderStatuses]
  );

  const exportContent = () => (
    <Row>
      <Col>
        The orders listed above are missing Data and/or Documents. They are
        currently on-hold. Please complete the End users so we can continue to
        fulfil these orders
      </Col>
    </Row>
  );

  const successOrderAlert = () => (
    <Alert variant="success">
      <p>Dear customer,</p>Thank you for your order.
      <p className="mb-0">
        Please review the list below for your orders where we are missing data
        or documents.
      </p>
    </Alert>
  );

  return (
    <>
      {fromCheckout ? successOrderAlert() : null}
      <Card>
        <Card.Header className="bg-didxl text-white">
          Orders with Incomplete End users
        </Card.Header>
        <Card.Body>
          <BootstrapGenericTable
            columns={columns}
            useData={useData}
            filterData={filterData}
            tableProps={() => ({
              responsive: true,
              bordered: true,
              className: 'normalFontSize',
            })}
            hiddenColumns="area,country,uuid"
            exportContent={exportContent}
            paginated
          />
        </Card.Body>
      </Card>
    </>
  );
};
